'use client'

import { useTranslation } from '../../hooks/useTranslation';
import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export default function PopularBrands({ homeData }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const sliderRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  
  const brands = homeData?.brands || [
    { id: 1, name: 'Ferrari', logo: '/images/b1.png' },
    { id: 2, name: 'Jaguar', logo: '/images/b2.png' },
    { id: 3, name: 'BMW', logo: '/images/b3.png' },
    { id: 4, name: 'Bentley', logo: '/images/b4.png' },
    { id: 5, name: 'Ferrari', logo: '/images/b1.png' },
    { id: 6, name: 'Jaguar', logo: '/images/b2.png' },
    { id: 7, name: 'BMW', logo: '/images/b3.png' },
    { id: 8, name: 'Bentley', logo: '/images/b4.png' },
  ];

  const checkScrollButtons = () => {
    if (sliderRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = sliderRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  };

  const scrollLeft = () => {
    if (sliderRef.current) {
      const cardWidth = sliderRef.current.children[0]?.offsetWidth || 200;
      sliderRef.current.scrollBy({ left: -cardWidth * 2, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (sliderRef.current) {
      const cardWidth = sliderRef.current.children[0]?.offsetWidth || 200;
      sliderRef.current.scrollBy({ left: cardWidth * 2, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    checkScrollButtons();
    const slider = sliderRef.current;
    if (slider) {
      slider.addEventListener('scroll', checkScrollButtons);
      window.addEventListener('resize', checkScrollButtons);
      return () => {
        slider.removeEventListener('scroll', checkScrollButtons);
        window.removeEventListener('resize', checkScrollButtons);
      };
    }
  }, []);

  return (
    <section className="py-8 sm:py-12 md:py-16 lg:py-20 bg-[var(--background)]">
      <div className="mx-auto px-4 sm:px-6 md:px-8 lg:px-16 xl:px-24 2xl:px-32">
        {/* Header with blue accent line */}
        <div className="mb-8 sm:mb-10 md:mb-12">
          <div className="flex items-center mb-3 sm:mb-4">
            <div className="w-8 sm:w-10 md:w-20 h-0.5 bg-[#006FFF] mr-3 sm:mr-4"></div>
            <h2 className="text-2xl sm:text-3xl font-bold font-poppins font-medium not-italic text-[32px] leading-[100%] tracking-[0] text-center">{t('brands.title')}</h2>
          </div>
          <p className="text-white max-w-md text-sm leading-relaxed">
            {t('brands.desc')}
          </p>
        </div>

        {/* Slider Container */}
        <div className="relative">
          {/* Navigation Buttons */}
          <button
            onClick={scrollLeft}
            disabled={!canScrollLeft}
            className={`absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-[#006FFF] hover:bg-blue-700 text-white w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
              !canScrollLeft ? 'opacity-50 cursor-not-allowed' : 'hover:scale-110'
            } hidden sm:flex`}
            aria-label="Scroll left"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <button
            onClick={scrollRight}
            disabled={!canScrollRight}
            className={`absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-[#006FFF] hover:bg-blue-700 text-white w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
              !canScrollRight ? 'opacity-50 cursor-not-allowed' : 'hover:scale-110'
            } hidden sm:flex`}
            aria-label="Scroll right"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>

          {/* Brand logos slider */}
          <div
            ref={sliderRef}
            className="flex gap-3 sm:gap-4 md:gap-5 lg:gap-6 overflow-x-auto scrollbar-hide pb-4 px-0 sm:px-12"
            style={{
              scrollbarWidth: 'none',
              msOverflowStyle: 'none'
            }}
            onScroll={checkScrollButtons}
          >
            {brands.map((brand) => (
              <div
                key={brand.id}
                onClick={() => navigate(`/cars?brand_ids[]=${brand.id}&lang=en&per_page=20&page=1&currency=usd`)}
                className="flex-shrink-0 w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 lg:w-32 lg:h-32 bg-[#5757578f] border border-[#666666] rounded-lg flex items-center justify-center hover:bg-gray-700 hover:border-[#888888] transition-all duration-300 cursor-pointer group p-2 sm:p-3 md:p-4"
              >
                <img 
                  src={brand.logo} 
                  alt={`${brand.name} logo`} 
                  className="max-h-8 sm:max-h-10 md:max-h-12 lg:max-h-16 w-auto object-contain filter brightness-90 group-hover:brightness-100 group-hover:scale-110 transition-all duration-300"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Mobile scroll indicators */}
        <div className="flex justify-center mt-4 sm:hidden">
          <div className="flex space-x-2">
            {Array.from({ length: Math.ceil(brands.length / 3) }).map((_, index) => (
              <div
                key={index}
                className="w-2 h-2 rounded-full bg-gray-600"
              ></div>
            ))}
          </div>
        </div>

      </div>

      <style jsx>{`
        .scrollbar-hide {
          -webkit-overflow-scrolling: touch;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </section>
  )
}